import {Polygon} from 'geojson';
import {DeepNullable} from 'ts-essentials';
import {LngLat} from '../geo';
import {FarmCustomColumns, HarvestCustomColumns, PolicyCustomColumns} from '../models/CustomColumns';
import {FarmData, HarvestData} from '../models/data';
import {Farm, HarvestYear, Policy, PolicyStatus} from '../models/interfaces';
import {AreaValue, Loss, UnitPriceValue, YieldValue} from '../models/types';

export class ImportedData {
  farms: ImportedFarm[];

  constructor(data: null | ImportedData) {
    this.farms = data ? data.farms.map(farm => new ImportedFarm(farm)) : [];
  }
}

export class ImportedFarm implements DeepNullable<FarmData> {
  // farm_id == null means that this farm will be inserted.
  // farm_id != null means that the farm will be *updated* with any non-null columns, and any fields will be inserted
  //  to that farm.
  farm_id: null | string;
  external_farm_id: null | string;
  telepac_id: null | string;
  farm_name: null | string;
  farmer_email: null | string;
  farmer_name: null | string;
  address: null | string;
  farm_location: null | LngLat;
  // Note that if we are importing a claim / visit for this farm, then farmHarvests.losses will be assumed to be the
  // complete set of losses for that claim. If we dedupe it, then we will delete any losses that do not appear there.
  // If we are importing a farm without a claim, then farmHarvests.losses will be ignored.
  farmHarvests: ImportedHarvest[];
  fields: ImportedField[];
  policy: null | ImportedPolicy;
  claim: null | ImportedClaim;
  editors: string[];
  user_group: null | string;
  comments: null | string;
  metadata: null | any;
  custom_columns: FarmCustomColumns;
  attachments: Farm['attachments'];
  merged_ids: null;

  constructor(farm: null | Partial<ImportedFarm>) {
    this.farm_id = farm?.farm_id ?? null;
    this.external_farm_id = farm?.external_farm_id ?? null;
    this.telepac_id = farm?.telepac_id ?? null;
    this.farm_name = farm?.farm_name ?? null;
    this.farmer_email = farm?.farmer_email ?? null;
    this.farmer_name = farm?.farmer_name ?? null;
    this.address = farm?.address ?? null;
    this.farm_location = farm?.farm_location ?? null;
    this.farmHarvests = farm?.farmHarvests?.map(harvest => new ImportedHarvest(harvest)) ?? [];
    this.fields = farm?.fields?.map(field => new ImportedField(field)) ?? [];
    this.claim = farm?.claim ?? null;
    this.user_group = farm?.user_group ?? null;
    this.editors = farm?.editors ?? [];
    this.comments = farm?.comments ?? null;
    this.metadata = farm?.metadata ?? null;
    this.custom_columns = new FarmCustomColumns(farm?.custom_columns ?? null);
    this.attachments = farm?.attachments ?? [];
    this.merged_ids = null;
    this.policy = farm?.policy ?? null;
  }
}

export function getImportedFarmData(farm: ImportedFarm): FarmData {
  return {
    address: farm.address,
    comments: farm.comments,
    editors: farm.editors,
    external_farm_id: farm.external_farm_id,
    telepac_id: farm.telepac_id,
    farm_location: farm.farm_location,
    farm_name: farm.farm_name,
    farmer_email: farm.farmer_email,
    farmer_name: farm.farmer_name,
    metadata: farm.metadata,
    custom_columns: farm.custom_columns,
    user_group: farm.user_group ?? '',
    attachments: farm.attachments,
    merged_ids: null,
  };
}

export class ImportedField {
  // field_id == null means that this field will be inserted.
  // field_id != null means that the field will be *updated* with any non-null columns, and any harvests will be inserted
  //  to that field.
  field_id: null | string;

  external_field_id: null | string;
  field_shape: null | Polygon;
  field_location: null | LngLat;
  field_area: null | AreaValue;
  harvests: ImportedHarvest[];

  constructor(field: null | Partial<ImportedField>) {
    this.field_id = field?.field_id ?? null;
    this.external_field_id = field?.external_field_id ?? null;
    this.field_shape = field?.field_shape ?? null;
    this.field_location = field?.field_location ?? null;
    this.field_area = field?.field_area ?? null;
    this.harvests = field?.harvests?.map(x => new ImportedHarvest(x)) ?? [];
  }
}

export class ImportedHarvest implements DeepNullable<Omit<HarvestData, 'farm_id' | 'field_id' | 'policy_id'>> {
  // harvest_id == null means that this harvest will be inserted.
  // harvest_id != null means that the harvest will be *updated* with any non-null columns.
  harvest_id?: null | string;
  crop_id: null | string;
  harvest_year: null | HarvestYear;
  aux_key: null | string;
  organic: null | boolean;
  irrigated: null | boolean;
  harvest_area: null | AreaValue;
  comments: null | string;
  insured_yield: null | YieldValue;
  insured_price: null | UnitPriceValue;
  insured_area: null | AreaValue;
  variety: null | string;
  metadata: null | any;
  custom_columns: HarvestCustomColumns;
  external_harvest_id: null | string;
  losses: Loss[];
  merged_ids: null;
  insured_percent: null | number;
  commodity_price: null | UnitPriceValue;
  reference_yield: null | YieldValue;
  premium_rate_percent: null | number;
  realized_yield: null | YieldValue;
  machinery_yield: null | YieldValue;
  weighed_yield: null | YieldValue;
  settlement_yield: null | YieldValue;
  constructor(harvest: null | Partial<ImportedHarvest>) {
    this.harvest_id = harvest?.harvest_id ?? null;
    this.crop_id = harvest?.crop_id ?? null;
    this.aux_key = harvest?.aux_key ?? null;
    this.variety = harvest?.variety ?? null;
    this.harvest_year = harvest?.harvest_year ?? null;
    this.organic = harvest?.organic ?? null;
    this.irrigated = harvest?.irrigated ?? null;
    this.insured_yield = harvest?.insured_yield ?? null;
    this.insured_price = harvest?.insured_price ?? null;
    this.insured_area = harvest?.insured_area ?? null;
    this.harvest_area = harvest?.harvest_area ?? null;
    this.comments = harvest?.comments ?? null;
    this.metadata = harvest?.metadata ?? null;
    this.custom_columns = new HarvestCustomColumns(harvest?.custom_columns ?? null);
    this.external_harvest_id = harvest?.external_harvest_id ?? null;
    this.losses = harvest?.losses ?? [];
    this.merged_ids = null;
    this.premium_rate_percent = harvest?.premium_rate_percent ?? null;
    this.commodity_price = harvest?.commodity_price ?? null;
    this.reference_yield = harvest?.reference_yield ?? null;
    this.insured_percent = harvest?.insured_percent ?? null;
    this.realized_yield = harvest?.realized_yield ?? null;
    this.machinery_yield = harvest?.machinery_yield ?? null;
    this.weighed_yield = harvest?.weighed_yield ?? null;
    this.settlement_yield = harvest?.settlement_yield ?? null;
  }
}

export function getImportedHarvestData(
  farm_id: string,
  field_id: null | string,
  harvest: ImportedHarvest,
  policy_id: null | string,
): HarvestData {
  return {
    field_id,
    farm_id: field_id ? null : farm_id,
    policy_id: policy_id ?? null,
    harvest_year: harvest.harvest_year,
    harvest_area: harvest.harvest_area,
    crop_id: harvest.crop_id,
    aux_key: harvest.aux_key,
    variety: harvest.variety,
    organic: harvest.organic,
    irrigated: harvest.irrigated,
    insured_area: harvest.insured_area,
    insured_yield: harvest.insured_yield,
    insured_price: harvest.insured_price,
    comments: harvest.comments,
    custom_columns: harvest.custom_columns,
    metadata: harvest.metadata,
    external_harvest_id: harvest.external_harvest_id,
    merged_ids: null,
    premium_rate_percent: harvest.premium_rate_percent,
    commodity_price: harvest.commodity_price,
    reference_yield: harvest.reference_yield,
    insured_percent: harvest.insured_percent,
    realized_yield: harvest.realized_yield,
    machinery_yield: harvest.machinery_yield,
    weighed_yield: harvest.weighed_yield,
    settlement_yield: harvest.settlement_yield,
  };
}

export class ImportedClaim {
  claim_id: null | string;
  claim_number: null | string;
  visit_id: null | string;
  assigned_to: string[];
  metadata: null | any;
  external_visit_id: null | string;
  custom_columns: null | object;
  harvest_year: HarvestYear;
  contact_first_name: null | string;
  contact_last_name: null | string;
  contact_phone: null | string;
  contact_email: null | string;

  constructor(claim: Partial<ImportedClaim> & {harvest_year: HarvestYear}) {
    this.claim_id = claim.claim_id ?? null;
    this.claim_number = claim.claim_number ?? null;
    this.visit_id = claim.visit_id ?? null;
    this.assigned_to = claim.assigned_to ?? [];
    this.external_visit_id = claim.external_visit_id ?? null;
    this.metadata = claim.metadata ?? null;
    this.custom_columns = claim.custom_columns ?? null;
    this.harvest_year = claim.harvest_year;
    this.contact_first_name = claim.contact_first_name ?? null;
    this.contact_last_name = claim.contact_last_name ?? null;
    this.contact_phone = claim.contact_phone ?? null;
    this.contact_email = claim.contact_email ?? null;
  }
}

// Note that user_group and editors are taken from the ImportedFarm.
export class ImportedPolicy {
  policy_id: null | string;
  custom_columns: null | PolicyCustomColumns;
  status: null | PolicyStatus;
  // The policy_number is not null in the database (sql/1-data/3-policy.sql:8), and for that we also make it not null
  // in the ImportedPolicy (if it was null, it couldn't be inserted to the db).
  policy_number: string;
  // Policy comments (e.g. contract type description)
  comments: null | string;

  constructor(policy: Partial<ImportedPolicy> & {policy_number: string}) {
    this.policy_id = policy?.policy_id ?? null;
    this.policy_number = policy.policy_number;
    this.comments = policy?.comments ?? null;
    this.custom_columns = new PolicyCustomColumns(policy?.custom_columns ?? null);
    this.status = policy?.status ?? null;
  }
}

export function getMatchingPolicies(
  transitiveGrantors: {[group: string]: undefined | Set<string>},
  policies: Policy[],
  farmUserGroup: string,
  policy_number: string,
): Policy[] {
  const transGroups: undefined | Set<string> = transitiveGrantors[farmUserGroup];
  return policies.filter(x => x.policy_number === policy_number && transGroups?.has(x.user_group));
}
